import { useEffect, useRef } from 'react';
import { Outlet, NavLink, Link, Navigate, useLocation } from 'react-router-dom';
import { PRIVACY_URL, SCORE_GUIDE_PATH, TERMS_URL } from './lib/publicLinks';
import SurfaceSwitcher from '~/components/shell/SurfaceSwitcher';
import LogoutButton from '~/components/shell/LogoutButton';
import LocaleSwitcher from '~/components/shell/LocaleSwitcher';
import ViewAsBanner from '~/components/shell/ViewAsBanner';
import { useSurfaces } from '~/components/shell/useSurfaces';
import { invalidateOnboardingGate, isOnboardingPath, useOnboardingGate } from '~/components/shell/useOnboardingGate';
import { syncPendingReferral } from '~/lib/referralSync';
import { syncAdAttribution } from '~/lib/adAttribution';
import { trackPageView } from '~/lib/activityTrack';
import { useInboxBadge } from './lib/useInboxBadge';
import { PendingReferralNotice } from './components/ReferralConsentNotice';
import PlanNudge from './components/PlanNudge';
import ConsentStep from './components/ConsentStep';
import { useT } from '~/i18n';

/**
 * El chrome del candidato. No hay riel como en tbit-canvas —estas pantallas son
 * documentos, no un tablero— pero la barra se comporta igual: vidrio sobre el
 * empapelado de marca, hairline en vez de sombra, y la ruta activa marcada en
 * púrpura como la espina del riel.
 *
 * CUATRO destinos y ni uno más. Antes eran cinco del candidato más cuatro de
 * operación colgadas al final, y la barra le pedía a una persona que busca
 * trabajo que leyera la palabra "Workers". Ahora la operación vive en /admin
 * con su propio shell y acá sólo queda lo que el candidato hace:
 *
 *   Panel          dónde va todo
 *   Mensajes       quién le contestó  (badge: lo único que espera por él)
 *   Postulaciones  qué mandamos       (era "Historial": un historial suena a
 *                                      archivo muerto, y es su lista de trabajo)
 *   Perfil         qué sabemos de él  (hoja de vida, preferencias y bolsa de
 *                                      talento son tres caras de lo mismo y se
 *                                      resuelven en tabs, no en tres rutas de
 *                                      primer nivel que compiten con Panel)
 *
 * ── LA BARRA DEL ALTA ES OTRA BARRA ────────────────────────────────────────
 *
 * Mientras el alta no esté hecha, esos cuatro destinos no llevan a ninguna
 * parte: Mensajes está vacío, Postulaciones también, y Panel rebota a
 * /onboarding en cuanto carga. Durante el alta la barra es el logo y "Salir",
 * y el switcher tampoco se dibuja —cambiar de sombrero no es la tarea de quien
 * todavía no terminó de ponerse el primero—. Al completar, aparece entera.
 *
 * La condición la calcula `useOnboardingGate`, que es también quien decide el
 * parpadeo: mientras no se sabe, se dibuja la barra MÍNIMA. Dibujar la completa
 * y quitarla se lee como una app que se equivocó.
 *
 * ── Y LA PUERTA ────────────────────────────────────────────────────────────
 *
 * Esconder los enlaces no alcanza: /mensajes escrito a mano, o pegado en un
 * correo viejo, seguía abriendo una pantalla que iba a fallar sola. Con el alta
 * a medias, toda ruta que no sea del alta vuelve a /onboarding. Sólo cuando el
 * servidor CONTESTÓ que falta (`known`): un timeout no puede mandar a rehacer
 * el alta a quien ya la tiene.
 */
export default function JobApplyLayout() {
  const { pathname } = useLocation();
  const t = useT('common');
  const gate = useOnboardingGate();
  const { surfaces } = useSurfaces();
  const unread = useInboxBadge(gate.complete);

  /**
   * El referido guardado por el navegador, estampado ahora que hay a quién.
   *
   * Acá y no en `AuthGuard` ni en el callback del OIDC: el backend cuelga la
   * atribución de la MEMBRESÍA, y en el callback todavía puede no existir —se
   * crea al guardar la hoja de vida—. Un POST antes de tiempo se contestaría
   * con un 4xx, y un 4xx quema el código pendiente para siempre (es la señal de
   * "este código no sirve"). `gate.complete` es canal + hoja de vida, así que
   * cuando es true la membresía existe seguro; `gate.known` exige además que la
   * respuesta sea FRESCA del servidor y no el recuerdo de la pestaña.
   *
   * Es del shell del candidato a propósito: la consola de agencia y la de
   * operación tienen sus propios layouts y este programa no es de ellas.
   *
   * `syncPendingReferral` es idempotente y silenciosa: sin código pendiente
   * —el caso de casi todo el mundo— no sale ninguna petición.
   */
  useEffect(() => {
    if (gate.known && gate.complete) void syncPendingReferral();
  }, [gate.known, gate.complete]);

  /**
   * De qué anuncio llegó, apenas hay membresía (no espera al alta completa: el
   * registro es justo lo que se quiere reportar). La membresía nace al darle
   * «Empezar» en /onboarding, así que se intenta en cada cambio de ruta hasta
   * que el servidor la acepte; después no sale nada. Ver `lib/adAttribution.ts`.
   */
  useEffect(() => {
    void syncAdAttribution();
  }, [pathname, gate.complete]);

  /**
   * Una visita por cambio de ruta para la actividad diaria. Aparte del efecto de
   * arriba a propósito: ése se repite cuando `gate.complete` cambia y aquí eso
   * contaría dos veces la misma página. Ver `lib/activityTrack.ts`.
   */
  const redirectingToOnboarding = gate.known && !gate.complete && !isOnboardingPath(pathname);
  const trackedPath = useRef<string | null>(null);
  useEffect(() => {
    // Esperar a que el gate sepa: si la ruta es una de las que el layout está
    // por redirigir a /onboarding, la página nunca se vio y no cuenta.
    if (!gate.known || redirectingToOnboarding) return;
    if (trackedPath.current === pathname) return;
    trackedPath.current = pathname;
    void trackPageView(pathname);
  }, [pathname, gate.known, redirectingToOnboarding]);

  if (redirectingToOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Cuentas que ya existían: el consentimiento aparece UNA vez, en lugar de la
  // página (cerrarlo no escribe nada y no vuelve a salir en esta sesión). El alta
  // lo pide por su cuenta.
  const showConsent = gate.known && gate.complete && gate.needsConsent && !isOnboardingPath(pathname);

  return (
    <div className="shell">
      <header className="shell__bar">
        {/* Durante el alta el logo vuelve al alta: es la única pantalla suya. */}
        <Link to={gate.complete ? '/dashboard' : '/onboarding'} className="shell__mark">
          <img src="/brand/mascot-96.png" alt="" width={30} height={30} />
          TBit <span>Jobs</span>
        </Link>
        {gate.complete && <SurfaceSwitcher surfaces={surfaces} />}
        <nav className="shell__nav" aria-label={t.candidateShell.navLabel}>
          {gate.complete && (
            <>
              <NavLink to="/dashboard" className="shell__link">{t.candidateShell.dashboard}</NavLink>
              <NavLink to="/mensajes" className="shell__link">
                {t.candidateShell.messages}
                {unread > 0 && (
                  /* Coral: es algo que ESPERA por el candidato. El mint está
                     reservado para el dato confirmado, y un número que exige
                     atención no es un logro. El texto del aria lo dice completo
                     porque "Mensajes 3" leído por un lector de pantalla no
                     distingue un contador de un nombre de sección. */
                  <span className="nav-badge" aria-label={t.candidateShell.unread(unread)}>
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </NavLink>
              <NavLink to="/postulaciones" className="shell__link">{t.candidateShell.applications}</NavLink>
              <NavLink to="/perfil" className="shell__link">{t.candidateShell.profile}</NavLink>
            </>
          )}
          {/* Al final del nav y en tinta apagada: siempre disponible, nunca lo
              que la barra propone. En un teléfono prestado es lo primero que se
              busca, y hasta hoy no existía en ninguna pantalla. El idioma va
              antes, también en la barra mínima del alta: quien no lee español
              tiene que poder cambiarlo desde la primera pantalla. */}
          <LocaleSwitcher />
          <LogoutButton />
        </nav>
      </header>
      <main className="shell__main">
        {/* Quien llegó con el código de un socio lo lee desde la primera
            pantalla del alta: el socio va a ver su correo y su plan. */}
        <PendingReferralNotice />
        {/* Sin plan activo no sale nada: se dice en cada pantalla y al abrir. */}
        {gate.complete && <PlanNudge plan={gate.plan} freeTier={gate.freeTier === true} />}
        {showConsent ? (
          <ConsentStep
            freeTier={gate.freeTier === true}
            onClose={gate.dismissConsent}
            onDone={() => {
              invalidateOnboardingGate();
              gate.dismissConsent();
            }}
          />
        ) : (
          <Outlet />
        )}
      </main>
      {/* La guía del puntaje la sirve job-apply-service en /empleos (HTML, no la
          SPA): por eso es un <a> y no un <Link>. */}
      <footer className="shell__foot">
        <nav aria-label={t.candidateShell.footerLabel}>
          <a href={SCORE_GUIDE_PATH}>{t.candidateShell.scoreGuide}</a>
          <a href={TERMS_URL} target="_blank" rel="noopener">{t.candidateShell.terms}</a>
          <a href={PRIVACY_URL} target="_blank" rel="noopener">{t.candidateShell.privacy}</a>
        </nav>
      </footer>
      {/* «Ver como» del admin: un operador mirando esta cuenta en solo lectura.
          Pegada abajo y no arriba: arriba la barra también es sticky y la tapaba. */}
      <ViewAsBanner />
    </div>
  );
}
